import React from "react";
import "../styles/Waiver.css";

export default function LiabilityWaiver() {
  return (
    <div className="waiver-root" data-theme="dark">

      {/* ── Navbar ── */}
      <nav className="waiver-nav">
        <a href="/" className="waiver-nav-back">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          RETURN TO VEGAS KARTS
        </a>
        <button className="waiver-print-btn" onClick={() => window.print()}>
          ⎙ PRINT / SAVE PDF
        </button>
      </nav>

      {/* ── Main ── */}
      <main className="waiver-container">

        {/* Draft banner */}
        <div className="waiver-draft-notice">
          <span className="waiver-draft-icon">⚠</span>
          <span>DRAFT — REVIEW WITH LICENSED ATTORNEY BEFORE USE. This waiver template is for informational purposes only and does not constitute legal advice. Consult a licensed Nevada attorney before using this document in connection with actual operations.</span>
        </div>

        {/* Hero */}
        <header className="waiver-hero">
          <div className="waiver-hero-bg" aria-hidden="true">
            <div className="waiver-hero-lines" />
            <div className="waiver-hero-glow" />
          </div>
          <div className="waiver-eyebrow">// PARTICIPANT LEGAL DOCUMENTATION</div>
          <h1 className="waiver-title">
            PARTICIPANT<br />
            WAIVER &amp; <span className="waiver-title-accent">ASSUMPTION</span><br />
            OF RISK
          </h1>
          <div className="waiver-meta-row">
            <span className="waiver-meta-chip">Vegas Karts LLC</span>
            <span className="waiver-meta-chip">Version 1.0 — March 2026</span>
            <span className="waiver-meta-chip">Las Vegas, Nevada</span>
          </div>
        </header>

        {/* Consent notice */}
        <div className="waiver-consent-banner">
          PLEASE READ THIS AGREEMENT CAREFULLY BEFORE PURCHASING A PRE-ORDER OR PARTICIPATING IN THE VEGAS KARTS EXPERIENCE. BY ACCEPTING A PRE-ORDER, CLICKING "AGREE," OR PARTICIPATING IN THE EXPERIENCE, YOU AGREE TO ALL TERMS BELOW.
        </div>

        {/* ── Sections ── */}

        <h2><span className="section-num">§1 </span>Pre-Order Acknowledgment</h2>
        <p>This Waiver applies to activities upon the commencement of service, estimated <strong>Winter 2026</strong>. Completing a pre-order reservation does not guarantee service availability or a specific date. The terms and obligations of this Waiver are triggered at the time of actual participation. However, acknowledging this Waiver at the time of pre-order confirms your understanding of the risks involved.</p>

        <h2><span className="section-num">§2 </span>Vehicle Acknowledgment</h2>
        <p>I understand that the Vegas Karts experience utilizes the <strong>DF Goblin kit car platform</strong>, an open-wheel, mid-engine, street-legal vehicle built on a donor car chassis. This is <strong>not</strong> a traditional go-kart or enclosed vehicle. The DF Goblin is an open-cockpit, purpose-built performance vehicle that operates on public roads alongside regular traffic. I understand that driving this vehicle involves significantly greater risk than driving a standard automobile or riding in a conventional go-kart.</p>

        <h2><span className="section-num">§3 </span>Driver's License &amp; Age Requirement</h2>
         <p>I affirm that I am <strong>18 years of age or older</strong> and hold a <strong>valid, unexpired driver's license</strong> issued by a U.S. state or jurisdiction recognized in Nevada. I understand that I must present my valid driver's license at the time of participation. Inability to produce a valid license will result in forfeiture of my reservation without refund.</p>

        <h2><span className="section-num">§4 </span>Assumption of Risk</h2>
        <p className="caps">I voluntarily assume all risks associated with the Vegas Karts experience, including but not limited to:</p>
        <ul className="waiver-list">
          <li><strong>Traffic and road hazards</strong> including other vehicles, pedestrians, debris, construction, and road conditions</li>
          <li><strong>Vehicle mechanical failure</strong> or malfunction</li>
          <li><strong>Collisions</strong> with other vehicles, objects, or persons</li>
          <li><strong>Weather conditions</strong> including reduced visibility, wet roads, and extreme heat</li>
          <li><strong>Loss of vehicle control</strong></li>
          <li><strong>Personal injury including serious injury or death</strong></li>
          <li><strong>Property damage</strong></li>
          <li>Any other risks inherent to operating an open-wheel vehicle on public roads in an urban or rural environment</li>
        </ul>
        <p>I acknowledge that no amount of instruction, supervision, or safety equipment can eliminate these risks entirely.</p>

        <h2><span className="section-num">§5 </span>Release &amp; Waiver of Liability</h2>
        <div className="waiver-warning-block">
          IN CONSIDERATION OF BEING PERMITTED TO PARTICIPATE IN THE VEGAS KARTS EXPERIENCE, I HEREBY RELEASE, WAIVE, DISCHARGE, AND COVENANT NOT TO SUE VEGAS KARTS LLC, ITS MEMBERS, OFFICERS, EMPLOYEES, AGENTS, CONTRACTORS, SUCCESSORS, AND ASSIGNS (COLLECTIVELY "RELEASED PARTIES") FROM ANY AND ALL LIABILITY, CLAIMS, DEMANDS, ACTIONS, OR CAUSES OF ACTION ARISING OUT OF OR RELATED TO ANY LOSS, DAMAGE, OR INJURY, INCLUDING DEATH, THAT MAY BE SUSTAINED BY ME OR ANY PROPERTY BELONGING TO ME, WHETHER CAUSED BY THE NEGLIGENCE OF THE RELEASED PARTIES OR OTHERWISE, WHILE PARTICIPATING IN THE VEGAS KARTS EXPERIENCE.
        </div>

        <h2><span className="section-num">§6 </span>Indemnification</h2>
        <p>I agree to indemnify, defend, and hold harmless the Released Parties from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from: (a) my participation in the Vegas Karts experience; (b) my breach of this Agreement; (c) my violation of applicable law; or (d) injury or damage caused by my operation of a Vegas Karts vehicle to third parties or their property.</p>

        <h2><span className="section-num">§7 </span>Health &amp; Fitness Acknowledgment</h2>
        <p>I confirm that I am in good physical health and have no medical condition, physical limitation, or impairment that would prevent me from safely operating a motor vehicle. I understand that operating an open-wheel vehicle on public roads requires full physical and cognitive capacity. I accept responsibility for disclosing any relevant health conditions to Vegas Karts staff prior to participation.</p>

        <h2><span className="section-num">§8 </span>Drug &amp; Alcohol Prohibition</h2>
        <div className="waiver-callout orange">
          <div className="waiver-callout-label">ZERO TOLERANCE POLICY</div>
          <p>I agree that I will not operate a Vegas Karts vehicle while under the influence of alcohol, illegal drugs, prescription medication that impairs driving ability, or any other substance that affects cognitive or physical performance. Vegas Karts reserves the right to refuse participation to any individual who appears impaired, without refund.</p>
        </div>

        <h2><span className="section-num">§9 </span>GPS &amp; Vehicle Monitoring</h2>
        <p>I acknowledge that Vegas Karts vehicles may be equipped with GPS tracking, telemetry systems, and/or cameras for safety monitoring, route compliance, and operational purposes. Data collected is used solely for operational safety and is not shared with third parties except as required by law.</p>

        <h2><span className="section-num">§10 </span>Photo, Video &amp; Media Release</h2>
        <p>I grant Vegas Karts LLC permission to capture, use, and publish photographs, video footage, and other media of me taken during the Vegas Karts experience for marketing, promotional, educational, and social media purposes, without additional compensation. I waive any right to review or approve such materials prior to use.</p>

        <h2><span className="section-num">§11 </span>Rules of Conduct</h2>
        <p>I agree to follow all instructions provided by Vegas Karts staff, obey all applicable traffic laws, stay on designated routes, and operate my vehicle in a safe and controlled manner at all times. Failure to comply may result in immediate termination of my experience without refund.</p>

        <h2><span className="section-num">§12 </span>Governing Law</h2>
        <p>This Agreement shall be governed by and construed in accordance with the laws of the <strong>State of Nevada</strong>. Any dispute arising under this Agreement shall be resolved in the courts of <strong>Clark County, Nevada</strong>.</p>

        <h2><span className="section-num">§13 </span>Severability</h2>
        <p>If any provision of this Agreement is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.</p>

        <h2><span className="section-num">§14 </span>Entire Agreement</h2>
        <p>This Agreement constitutes the entire agreement between the parties with respect to assumption of risk and liability, and supersedes all prior agreements, representations, or understandings, whether written or oral, on the same subject.</p>

        <h2><span className="section-num">§15 </span>Attorney Review Disclaimer</h2>
        <div className="waiver-callout blue">
          <div className="waiver-callout-label">ADVISORY NOTICE</div>
          <p><strong>YOU ARE ADVISED TO CONSULT WITH A LICENSED NEVADA ATTORNEY BEFORE SIGNING THIS WAIVER. BY PROCEEDING, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND VOLUNTARILY AGREE TO ALL TERMS OF THIS WAIVER AND ASSUMPTION OF RISK AGREEMENT.</strong></p>
        </div>

        <h2><span className="section-num">§16 </span>Electronic Signature Acknowledgment</h2>
        <p>Pursuant to the Nevada Electronic Transactions Act (NRS Chapter 719) and the Electronic Signatures in Global and National Commerce Act (E-SIGN Act), I acknowledge that my electronic acceptance of this Agreement (whether by checkbox, button click, or digital signature) constitutes a legally binding signature with the same legal force and effect as a handwritten signature.</p>

        {/* ── Signature Block ── */}
        <div class="sig-block">
          <h3>// PARTICIPANT SIGNATURE BLOCK (PRINT VERSION)</h3>
          <div class="sig-row">
            <div class="sig-field">
              <div class="sig-label">FULL LEGAL NAME (PRINT)</div>
              <div class="sig-line"></div>
            </div>
            <div class="sig-field">
              <div class="sig-label">DATE</div>
              <div class="sig-line"></div>
            </div>
          </div>
          <div class="sig-row">
            <div class="sig-field">
              <div class="sig-label">SIGNATURE</div>
              <div class="sig-line"></div>
            </div>
            <div class="sig-field">
              <div class="sig-label">DRIVER'S LICENSE NUMBER &amp; STATE</div>
              <div class="sig-line"></div>
            </div>
          </div>
          <div class="sig-row">
            <div class="sig-field">
              <div class="sig-label">EMERGENCY CONTACT NAME</div>
              <div class="sig-line"></div>
            </div>
            <div class="sig-field">
              <div class="sig-label">EMERGENCY CONTACT PHONE</div>
              <div class="sig-line"></div>
            </div>
          </div>
        </div>

      </main>

      {/* ── Footer ── */}
      <footer className="waiver-footer">
        <div className="waiver-footer-line" />
        <div className="waiver-footer-inner">
          <span>© 2026 Vegas Karts LLC · All rights reserved</span>
          <div className="waiver-footer-links">
            <a href="/terms">Terms of Service</a>
            <span className="waiver-footer-dot">·</span>
            <a href="/">Return to Site</a>
          </div>
        </div>
      </footer>

    </div>
  );
}